import type { ApprovalResolution, PendingApproval } from "@developerz.ai/ggcoin-sdk";
import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { Money } from "../components/Money.tsx";
import { formatTime } from "../lib/time.ts";

/**
 * The master's approval inbox (#29). Above-threshold / agent spends park here;
 * approving a transfer completes the send, denying cancels it. Live via the
 * wallet WS channel.
 */
export function Approvals() {
  const { client } = useAuth();
  const [pending, setPending] = createSignal<PendingApproval[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<unknown | null>(null);
  // Per-id in-flight set so only the row being resolved is disabled.
  const [resolving, setResolving] = createSignal<ReadonlySet<string>>(new Set());

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setPending(await client.getPendingApprovals());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  onMount(() => {
    void load();
    let cancelled = false;
    let teardown: (() => Promise<void> | void) | null = null;
    void client
      .subscribeWallet({
        onEvent: (event) => {
          if (event.type === "wallet.approval-requested") void load();
        },
        onError: () => {},
      })
      .then((unsubscribe) => {
        if (cancelled) void unsubscribe();
        else teardown = unsubscribe;
      })
      .catch(() => {});
    onCleanup(() => {
      cancelled = true;
      if (teardown) void teardown();
    });
  });

  async function resolve(id: string, resolution: ApprovalResolution) {
    if (resolving().has(id)) return;
    setResolving((prev) => new Set(prev).add(id));
    setError(null);
    try {
      await client.resolveApproval(id, resolution);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setResolving((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  return (
    <section class="card">
      <h2>Pending approvals</h2>
      <p class="muted">
        Requests still awaiting your decision. When a sub-wallet or agent tries to spend above its
        approval threshold, it appears here. Approving a <strong>transfer</strong> completes the
        send right away — coin moves to the recipient and shows up in History; denying cancels it.
      </p>
      <ErrorBanner error={error()} />
      <Show
        when={pending().length > 0}
        fallback={
          <p class="muted" style={{ "margin-top": 0 }}>
            {loading() ? "Loading…" : "Nothing waiting — you're all caught up."}
          </p>
        }
      >
        <div class="table-scroll">
          <table class="table">
            <thead>
              <tr>
                <th>Amount</th>
                <th>Type</th>
                <th>From sub-wallet</th>
                <th>To</th>
                <th>Expires</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              <For each={pending()}>
                {(a) => (
                  <tr>
                    <td class="num">
                      <Money amount={a.amount} />
                    </td>
                    <td>
                      <span class={`badge badge-${a.kind === "transfer" ? "active" : "frozen"}`}>
                        {a.kind === "transfer" ? "transfer" : "spend"}
                      </span>
                    </td>
                    <td class="id-cell">{a.subWalletId}</td>
                    <td class="id-cell">{a.recipientWalletId ?? "—"}</td>
                    <td>{formatTime(a.expiresAt)}</td>
                    <td>
                      <div class="row-actions">
                        <button
                          type="button"
                          class="primary small"
                          disabled={resolving().has(a.id)}
                          onClick={() => resolve(a.id, "approve")}
                        >
                          {resolving().has(a.id) ? "…" : "Approve"}
                        </button>
                        <button
                          type="button"
                          class="secondary small"
                          disabled={resolving().has(a.id)}
                          onClick={() => resolve(a.id, "deny")}
                        >
                          Deny
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </section>
  );
}
